import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, CardBody, CardHeader, Col, Collapse, Row, Spinner } from 'reactstrap';
import { ChapterItem, useChaptersJson } from '../hooks/useChaptersJson';
import { useExecutionInfo } from '../hooks/useExecutionInfo';
import { useStoreActions } from '../stores/store';
import { secondsToTimeString } from '../utils/secondsToTimeString';

type ExtendedChapterItem = ChapterItem & { idx: number };

const ExecutionInfoChapters: React.FC<{ id: string }> = ({ id }) => {
  const { data: executionData } = useExecutionInfo(id)
  const { data, isLoading, isError } = useChaptersJson(executionData?.chaptersUrl)
  const seekTo = useStoreActions(actions => actions.seekTo)
  const [collapsed, setCollapsed] = useState(true)
  const [searchString, setSearchString] = useState('')
  const [filteredData, setFilteredData] = useState<ExtendedChapterItem[]>([])
  const [searched, setSearched] = useState(false)

  const initialChapters = useMemo<ExtendedChapterItem[]>(() => {
    if (data && data.length > 0) {
      return data.map((c, idx) => ({
        ...c, idx
      }))
    }
    return []
  }, [data])

  useEffect(() => {
    setFilteredData(initialChapters)
  }, [initialChapters])

  const goToTopAndSeek = (ms: number) => {
    window.scrollTo(0, 0)
    seekTo(ms)
  }

  const clearSearch = () => {
    setSearched(false)
    setFilteredData(initialChapters)
  }

  const handleSearch = () => {
    if (!searchString) {
      clearSearch()
      return
    }
    if (collapsed) {
      setCollapsed(false)
    }
    setSearched(true)
    setFilteredData(initialChapters.filter(c => {
      const regex = new RegExp(`.*${searchString}.*`, 'ig')
      return regex.test(c.summary) || regex.test(c.gist) || regex.test(c.headline)
    }))
  }

  let content: JSX.Element | null = null

  if (isLoading) {
    content = <Spinner />
  } else if (isError) {
    content = (
      <Alert color="danger">
        Something went wrong!
      </Alert>
    )
  } else if (!data) {
    content = (
      <Alert color="warning">
        No chapters available yet.
      </Alert>
    )
  } else {
    content = (
      <>
        {
          searched && filteredData.length === 0 &&
          <Alert color="warning">
            No results found!
          </Alert>
        }
        {
          filteredData.map((f, i) => (
            <div key={i}>
              <h3>Chapter {f.idx + 1}: {f.gist}</h3>
              <p>Headline: {f.headline}</p>
              <p className='text-dark'>Summary: {f.summary}</p>
              <small>
                Talked at: {secondsToTimeString(f.start / 1000)}
              </small>
              <div></div>
              <Button
                onClick={() => goToTopAndSeek(f.start)}
                size="sm"
                color="primary"
              >
                Seek Here
              </Button>
              <hr />
            </div>
          ))
        }
      </>
    )
  }


  return (
    <>
      <Row>
        <Col className="order-xl-1">
          <Card className="bg-secondary shadow">
            <CardHeader className="bg-white border-0">
              <Row className="align-items-center">
                <Col xs="8" sm="3">
                  <h3 className="mb-0">({initialChapters.length}) Chapters</h3>
                </Col>
                <Col xs="8" sm="5">
                  <div className="input-group-alternative input-group">
                    <div className="input-group-prepend">
                      <span onClick={handleSearch} className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search for anything"
                        value={searchString}
                        onChange={e => {
                          setSearchString(e.target.value)
                        }}
                        onKeyDown={e => {
                          if (e.key.toLowerCase() === 'enter') {
                            handleSearch()
                          }
                        }}
                      />
                    </div>
                  </div>
                </Col>
                <Col className="text-right" xs="4">
                  <Button
                    size="sm"
                    color="primary"
                    onClick={() => {
                      setCollapsed(!collapsed)
                    }}
                  >
                    {collapsed ? 'Expand' : 'Collapse'}
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <Collapse isOpen={!collapsed}>
              <CardBody>
                {content}
              </CardBody>
            </Collapse>
          </Card>
        </Col>
      </Row>
      <br />
    </>
  );
}

export default ExecutionInfoChapters
